import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { AssetSvg } from "../../assets";
import { useFunctionalityCallRecording, useFunctionalityCallTranscription } from "../../pages/hooks";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  callId?: string;
  title?: string;
};

export function ModalCallRecording(props: Props): JSX.Element {
  const { show, handleShow, callId, title } = props;
  const {
    LoadByCallId: LoadRecordingByCallId,
    LoadAccessUrl: LoadRecordingAccessUrl
  } = useFunctionalityCallRecording();
  const {
    LoadByCallId: LoadTranscriptionByCallId,
    LoadAccessUrl: LoadTranscriptionAccessUrl
  } = useFunctionalityCallTranscription();
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [recordingId, setRecordingId] = useState<string>("");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [accessUrlExpiresAtUtc, setAccessUrlExpiresAtUtc] = useState<string>("");
  const [transcriptionText, setTranscriptionText] = useState<string>("");
  const [transcriptionId, setTranscriptionId] = useState<string>("");
  const [transcriptionLoading, setTranscriptionLoading] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState(false);
  const [transcriptionDownloadTxtLoading, setTranscriptionDownloadTxtLoading] = useState(false);
  const [transcriptionAccessUrl, setTranscriptionAccessUrl] = useState<string>("");
  const [transcriptionFormat, setTranscriptionFormat] = useState<string>("");

  useEffect(() => {
    if (!show || !callId) return;
    setIsLoading(true);
    setHasError(false);
    setAudioUrl("");
    setRecordingId("");
    setAccessUrlExpiresAtUtc("");
    LoadRecordingByCallId(callId)
      .then((data) => {
        const first = Array.isArray(data) ? data[0] : data;
        const url = first?.accessUrl ?? "";
        const id = first?.id ?? "";
        const expiresAt = first?.accessUrlExpiresAtUtc ?? "";
        setAudioUrl(url);
        setRecordingId(id);
        setAccessUrlExpiresAtUtc(expiresAt);
      })
      .catch(() => {
        setHasError(true);
      })
      .finally(() => setIsLoading(false));
  }, [LoadRecordingByCallId, callId, show]);

  useEffect(() => {
    if (!show || !callId) return;
    setTranscriptionLoading(true);
    setTranscriptionError(false);
    setTranscriptionText("");
    setTranscriptionId("");
    setTranscriptionAccessUrl("");
    setTranscriptionFormat("");
    LoadTranscriptionByCallId(callId)
      .then((data) => {
        const first = Array.isArray(data) ? data[0] : data;
        const id = first?.id ?? "";
        const url = first?.accessUrl ?? "";
        const format = first?.format ?? "";
        setTranscriptionId(id);
        setTranscriptionAccessUrl(url);
        setTranscriptionFormat(format);
      })
      .catch(() => {
        setTranscriptionError(true);
      })
      .finally(() => setTranscriptionLoading(false));
  }, [LoadTranscriptionByCallId, callId, show]);

  useEffect(() => {
    if (show) return;
    setIsLoading(false);
    setHasError(false);
    setAudioUrl("");
    setRecordingId("");
    setAccessUrlExpiresAtUtc("");
    setDownloadLoading(false);
    setTranscriptionText("");
    setTranscriptionId("");
    setTranscriptionLoading(false);
    setTranscriptionError(false);
    setTranscriptionDownloadTxtLoading(false);
    setTranscriptionAccessUrl("");
    setTranscriptionFormat("");
  }, [show]);

  useEffect(() => {
    if (!show || !callId || !accessUrlExpiresAtUtc) return;
    const expiresAtMs = Date.parse(accessUrlExpiresAtUtc);
    if (Number.isNaN(expiresAtMs)) return;

    const now = Date.now();
    const refreshInMs = Math.max(0, expiresAtMs - now - 5000);
    const timer = window.setTimeout(() => {
      LoadRecordingByCallId(callId).then((data) => {
        const first = Array.isArray(data) ? data[0] : data;
        const url = first?.accessUrl ?? "";
        const id = first?.id ?? "";
        const expiresAt = first?.accessUrlExpiresAtUtc ?? "";
        setAudioUrl(url);
        setRecordingId(id);
        setAccessUrlExpiresAtUtc(expiresAt);
      }).catch(() => {
        setHasError(true);
      });
    }, refreshInMs);

    return () => window.clearTimeout(timer);
  }, [LoadRecordingByCallId, accessUrlExpiresAtUtc, callId, show]);

  const canDownload = Boolean(recordingId);

  const handleDownload = async () => {
    if (!recordingId || downloadLoading) return;
    setDownloadLoading(true);
    try {
      const data = await LoadRecordingAccessUrl(recordingId, true, 30);
      const url = data?.url ?? "";
      if (url) {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.rel = "noopener";
        anchor.target = "_blank";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
    } catch {
      // no-op; error message is handled by backend errors and user can retry
    } finally {
      setDownloadLoading(false);
    }
  };

  const canDownloadTranscription = Boolean(transcriptionId);

  const handleTranscriptionDownload = async () => {
    if (!transcriptionId) return;
    if (transcriptionDownloadTxtLoading) return;
    setTranscriptionDownloadTxtLoading(true);
    try {
      const data = await LoadTranscriptionAccessUrl(transcriptionId, true, 30);
      const url = data?.url ?? "";
      if (url) {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.rel = "noopener";
        anchor.target = "_blank";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
    } catch {
      // no-op; error message is handled by backend errors and user can retry
    } finally {
      setTranscriptionDownloadTxtLoading(false);
    }
  };

  const canViewTranscription =
    Boolean(transcriptionAccessUrl) &&
    (transcriptionFormat || "").toLowerCase() === "txt";

  const handleLoadTranscriptionText = async () => {
    if (!canViewTranscription || transcriptionLoading) return;
    setTranscriptionLoading(true);
    setTranscriptionError(false);
    try {
      const res = await fetch(transcriptionAccessUrl);
      const text = await res.text();
      setTranscriptionText(text);
    } catch {
      setTranscriptionError(true);
    } finally {
      setTranscriptionLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => handleShow(false)}
      id="callRecordingModal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{title ?? "Call Media"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <details open>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>
              Call Recording
            </summary>
            <div style={{ marginTop: 8 }}>
              {isLoading ? (
                <div className="text-muted">Loading recording...</div>
              ) : hasError ? (
                <div className="text-danger">Failed to load recording.</div>
              ) : audioUrl ? (
                <div className="d-flex align-items-center gap-2">
                  <audio
                    controls
                    controlsList="nodownload"
                    className="flex-grow-1 mr-3"
                    style={{ marginTop: 8 }}
                  >
                    <source src={audioUrl} />
                    Your browser does not support the audio element.
                  </audio>
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center mt-2"
                    style={{ borderRadius: 10, padding: 12, boxShadow: "0 6px 16px rgba(0, 0, 0, 0.25)" }}
                    title="Download"
                    onClick={handleDownload}
                    disabled={!canDownload || downloadLoading}
                  >
                    <span className="" style={{ lineHeight: 0 }}>
                      <AssetSvg.Download style={{ width: 16, height: 16 }} />
                    </span>
                  </button>
                </div>
              ) : (
                <div className="text-muted">No recording is available for this call.</div>
              )}
            </div>
          </details>
          <details>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>
              Call Transcription
            </summary>
            <div style={{ marginTop: 12 }}>
              {transcriptionLoading ? (
                <div className="text-muted">Loading transcription...</div>
              ) : transcriptionError ? (
                <div className="text-danger">Failed to load transcription.</div>
              ) : transcriptionText ? (
                <>
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      maxHeight: 240,
                      overflow: "auto",
                      padding: 12,
                      borderRadius: 8,
                      border: "1px solid rgba(0,0,0,0.1)",
                      background: "rgba(0,0,0,0.02)",
                    }}
                  >
                    {transcriptionText}
                  </div>
                  <div className="d-flex align-items-center gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center"
                      onClick={handleTranscriptionDownload}
                      disabled={
                        !canDownloadTranscription ||
                        transcriptionDownloadTxtLoading
                      }
                    >
                      <span style={{ lineHeight: 0, marginRight: 6 }}>
                        <AssetSvg.Download style={{ width: 16, height: 16 }} />
                      </span>
                      Download
                    </button>
                  </div>
                </>
              ) : transcriptionAccessUrl ? (
                <>
                  <div className="text-muted mb-2">
                    Transcription file is available.
                  </div>
                  <div className="d-flex align-items-center w-100 justify-content-between gap-3 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-secondary d-flex align-items-center"
                      onClick={handleLoadTranscriptionText}
                      disabled={!canViewTranscription || transcriptionLoading}
                      title={
                        canViewTranscription
                          ? "Load transcription text"
                          : "Preview is available only for TXT"
                      }
                    >
                      View TXT
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center"
                      onClick={handleTranscriptionDownload}
                      disabled={
                        !canDownloadTranscription ||
                        transcriptionDownloadTxtLoading
                      }
                    >
                      <span style={{ lineHeight: 0, marginRight: 6 }}>
                        <AssetSvg.Download style={{ width: 16, height: 16 }} />
                      </span>
                      Download
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-muted">
                  No transcription is available for this call.
                </div>
              )}
            </div>
          </details>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary text-uppercase"
          onClick={() => handleShow(false)}
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
}
